import React from 'react'
import { Collapse, Descriptions, Divider, Drawer, Empty, Flex, Tag, Tooltip, Typography } from 'antd'
import { groupBy, orderBy } from 'lodash-es'
import {
  EntityAccessSummary,
  UserEmployeeDataAccessSummary,
} from '@/features/portal-configuration/employee-data-access-claim.def.ts'
import {
  SectionGroupLegend,
  SectionGroupTags,
} from '@/pages/portal-configuration/components/SectionGroupTags.tsx'

type Props = {
  open: boolean
  onClose: () => void
  user?: UserEmployeeDataAccessSummary
  /** The backend's live sensitive-section list, used to highlight within each section set. */
  sensitiveSections: string[]
}

/**
 * Bucket for entities whose string maps to no known country. Should not occur, but grouping on a
 * null key would silently drop those entities from the drawer, which is worse than showing them.
 */
const UNMAPPED_COUNTRY = 'Unmapped country'

type CountryGroup = {
  country: string
  entities: EntityAccessSummary[]
  /** True when ANY entity in the country grants HC sections. */
  hasHcAccess: boolean
  /** True when ANY entity in the country grants a sensitive section, in either set. */
  hasSensitiveAccess: boolean
}

/**
 * Groups a user's entities by country, ordered alphabetically with the unmapped bucket last.
 *
 * An employment entity belongs to exactly one country, so this is a clean partition rather than a
 * fan-out. Mirrors the country grouping in `useEmployeeDataAccessClaim`'s entity options.
 */
const groupEntitiesByCountry = (entities: EntityAccessSummary[]): CountryGroup[] => {
  const grouped = groupBy(entities, entity => entity.country ?? UNMAPPED_COUNTRY)

  return orderBy(
    Object.entries(grouped).map(([country, group]) => ({
      country,
      entities: orderBy(group, entity => entity.entity, 'asc'),
      hasHcAccess: group.some(entity => entity.hasHcAccess),
      hasSensitiveAccess: group.some(entity => entity.hasSensitiveAccess),
    })),
    [group => (group.country === UNMAPPED_COUNTRY ? 1 : 0), group => group.country],
    ['asc', 'asc'],
  )
}

/**
 * One entity: its name and flags, then the two section sets as grouped slugs.
 *
 * The General / HC pairing is the point — an employee's department decides which set applies, so
 * "sensitive in General but not HC" means Human Capital employees at this entity stay unreachable
 * while their colleagues do not.
 */
const EntityDetail: React.FC<{ entity: EntityAccessSummary, sensitiveSections: string[] }> = ({
  entity,
  sensitiveSections,
}) => (
  <Flex vertical gap="small">
    <Flex gap="small" align="center" wrap="wrap">
      <Typography.Text strong>{entity.entity}</Typography.Text>
      {entity.hasHcAccess
        ? (
          <Tooltip title="Can reach Human Capital department employees at this entity.">
            <Tag color="purple" className="m-0">HC</Tag>
          </Tooltip>
        )
        : null}
      {entity.hasSensitiveAccess
        ? <Tag color="red" className="m-0">Sensitive</Tag>
        : null}
    </Flex>
    <Descriptions
      column={1}
      size="small"
      items={[
        {
          key: 'general',
          label: (
            <Tooltip title="Applies to employees outside the Human Capital department.">
              General
            </Tooltip>
          ),
          children: (
            <SectionGroupTags
              sections={entity.generalSections}
              sensitiveSections={sensitiveSections}
            />
          ),
        },
        {
          key: 'hc',
          label: (
            <Tooltip title="Applies to Human Capital department employees. None means they are unreachable at this entity.">
              Human Capital
            </Tooltip>
          ),
          children: (
            <SectionGroupTags
              sections={entity.hcSections}
              sensitiveSections={sensitiveSections}
              emptyContent={(
                <Typography.Text type="secondary">
                  None — HC employees here are unreachable
                </Typography.Text>
              )}
            />
          ),
        },
      ]}
    />
  </Flex>
)

/**
 * Per-country breakdown for one user, one collapse panel per country.
 *
 * Grouped by country rather than flat by entity because several entities commonly share one country
 * (Hong Kong has three), and a user's grants usually cluster there. Each header carries the entity
 * count and the two flags aggregated across the country, so the panels often need not be opened.
 */
export const EmployeeDataAccessClaimDrawer: React.FC<Props> = ({
  open,
  onClose,
  user,
  sensitiveSections,
}) => {
  const countryGroups = user ? groupEntitiesByCountry(user.entities) : []

  return (
    <Drawer
      title={user ? `Employee Data Access — ${user.username}` : 'Employee Data Access'}
      open={open}
      onClose={onClose}
      width={760}
    >
      {!countryGroups.length
        ? (
          <Empty
            image={Empty.PRESENTED_IMAGE_SIMPLE}
            description="This user holds no employment entity, so the claim grants nothing."
          />
        )
        : (
          <Flex vertical gap="middle">
            <Typography.Text type="secondary">
              {user?.entityCount} employment {user?.entityCount === 1 ? 'entity' : 'entities'} across
              {' '}{countryGroups.length} {countryGroups.length === 1 ? 'country' : 'countries'}.
            </Typography.Text>
            <SectionGroupLegend />
            <Collapse
              items={countryGroups.map(group => ({
                key: group.country,
                label: (
                  <Flex gap="small" align="center" wrap="wrap">
                    <Typography.Text strong>{group.country}</Typography.Text>
                    <Typography.Text type="secondary">
                      {group.entities.length} {group.entities.length === 1 ? 'entity' : 'entities'}
                    </Typography.Text>
                    {group.hasHcAccess
                      ? (
                        <Tooltip title="Can reach Human Capital department employees somewhere in this country.">
                          <Tag color="purple" className="m-0">HC</Tag>
                        </Tooltip>
                      )
                      : null}
                    {group.hasSensitiveAccess
                      ? <Tag color="red" className="m-0">Sensitive</Tag>
                      : null}
                  </Flex>
                ),
                children: (
                  <Flex vertical>
                    {group.entities.map((entity, index) => (
                      <React.Fragment key={entity.entity}>
                        {index > 0 ? <Divider className="my-3" /> : null}
                        <EntityDetail entity={entity} sensitiveSections={sensitiveSections} />
                      </React.Fragment>
                    ))}
                  </Flex>
                ),
              }))}
            />
          </Flex>
        )}
    </Drawer>
  )
}
